import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../lib/translations';
import axios from 'axios';
import { FileText, ArrowLeft, Calculator } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedQuote() {
  const { language } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const ccy = t('common.currency', language);

  useEffect(() => {
    axios.get(`${API}/quotes/shared/${id}`)
      .then(res => setQuote(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#1B3A7B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center">
        <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-700 mb-2">
          {language === 'pl' ? 'Wycena nie znaleziona' : 'Quote not found'}
        </h2>
        <p className="text-slate-500 mb-6">
          {language === 'pl' ? 'Link mógł wygasnąć lub jest niepoprawny.' : 'The link may have expired or is invalid.'}
        </p>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-3 bg-[#1B3A7B] text-white rounded-md font-medium hover:bg-[#142d63] transition-colors inline-flex items-center gap-2"
        >
          <Calculator className="w-4 h-4" />
          {language === 'pl' ? 'Przejdź do kalkulatora' : 'Go to calculator'}
        </button>
      </div>
    );
  }

  const items = [
    { label: language === 'pl' ? 'Obsługa księgowa' : 'Accounting', desc: t(`step1.${quote.accounting_form}`, language), amount: quote.base_price },
    quote.vat_cost > 0 && { label: 'VAT', desc: language === 'pl' ? 'Deklaracja VAT-7' : 'VAT-7 declaration', amount: quote.vat_cost },
    quote.vat_eu_cost > 0 && { label: 'VAT EU', desc: language === 'pl' ? 'Deklaracja VAT EU' : 'VAT EU declaration', amount: quote.vat_eu_cost },
    quote.employee_cost > 0 && { label: language === 'pl' ? 'Pracownicy' : 'Employees', desc: `${quote.employee_count} os.`, amount: quote.employee_cost },
    quote.zus_cost > 0 && { label: 'ZUS', desc: '', amount: quote.zus_cost },
    quote.specifics_adjustment !== 0 && { label: language === 'pl' ? 'Specyfika' : 'Specifics', desc: '', amount: quote.specifics_adjustment },
    quote.additional_services_total > 0 && { label: language === 'pl' ? 'Usługi dodatkowe' : 'Additional', desc: '', amount: quote.additional_services_total },
  ].filter(Boolean);

  return (
    <div className="max-w-lg mx-auto px-4 py-8 sm:py-12" data-testid="shared-quote-page">
      <div className="text-center mb-8">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#1B3A7B] mb-1">
          {language === 'pl' ? 'Wycena usług księgowych' : 'Accounting Services Quote'}
        </h1>
        <p className="text-sm text-slate-500">
          {language === 'pl' ? 'Kurdynowski Accounting & Tax Solutions' : 'Kurdynowski Accounting & Tax Solutions'}
        </p>
      </div>

      <div className="receipt-card bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
        {/* Details */}
        <div className="p-6 space-y-2 text-sm text-slate-600">
          <div className="flex justify-between">
            <span>{language === 'pl' ? 'Forma' : 'Form'}:</span>
            <span className="font-medium text-slate-800">{t(`step1.${quote.accounting_form}`, language)}</span>
          </div>
          <div className="flex justify-between">
            <span>{language === 'pl' ? 'Podmiot' : 'Entity'}:</span>
            <span className="font-medium text-slate-800">{t(`step1.entities.${quote.legal_entity}`, language)}</span>
          </div>
          <div className="flex justify-between">
            <span>{language === 'pl' ? 'Dokumenty' : 'Documents'}:</span>
            <span className="font-medium text-slate-800">{quote.document_count}</span>
          </div>
        </div>

        {/* Items */}
        <div className="border-t border-slate-100 divide-y divide-slate-100">
          {items.map((item, i) => (
            <div key={i} className="px-6 py-3 flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-slate-700">{item.label}</span>
                {item.desc && <span className="text-xs text-slate-500 ml-2">{item.desc}</span>}
              </div>
              <span className="font-mono-price font-semibold text-sm text-slate-800">
                {item.amount > 0 ? '+' : ''}{item.amount} {ccy}
              </span>
            </div>
          ))}
        </div>

        {/* Total */}
        <div className="bg-[#1B3A7B] px-6 py-5 text-center">
          <div className="text-white/70 text-xs uppercase tracking-wider mb-1">
            {language === 'pl' ? 'Suma końcowa' : 'Grand total'}
          </div>
          <div className="font-mono-price text-3xl font-bold text-white">
            {quote.monthly_total} {ccy}
          </div>
          <div className="text-white/60 text-xs mt-1">
            {language === 'pl' ? 'netto / miesięcznie' : 'net / monthly'}
          </div>
        </div>
      </div>

      <div className="mt-8 text-center">
        <button
          onClick={() => navigate('/')}
          className="px-6 py-3 bg-[#E8941A] text-white rounded-md font-medium hover:bg-[#d68515] transition-colors inline-flex items-center gap-2"
          data-testid="shared-quote-calc-btn"
        >
          <Calculator className="w-4 h-4" />
          {language === 'pl' ? 'Oblicz swoją wycenę' : 'Calculate your quote'}
        </button>
      </div>
    </div>
  );
}
